// src/components/SearchComponent.tsx
import { DocumentNode, useQuery } from '@apollo/client'
import { ActionIcon, Autocomplete, CloseIcon } from '@mantine/core'
import { IconSearch } from '@tabler/icons-react'
import React, { useCallback, useEffect, useMemo } from 'react'

import useDebouncedSearch from '@/hooks/useDebouncedSearch'
import { GetSuggestedCollectionItems } from '@/schemas/collections.schema'
import {
  GetSuggestedCollections,
  GetSuggestedCreators,
  GetSuggestedItems,
} from '@/schemas/explore.schema'
import { filterFunction, sortFunction, suggestionFunction } from '@/schemas/functions'
import {
  GetSuggestedCreatedItems,
  GetSuggestedEarnedItems,
  GetSuggestedOwnedItems,
} from '@/schemas/tokens.schema'
import { ContentType } from '@/utils/curation'
import { itemNameWithId } from '@/utils/utils'

interface SearchComponentProps {
  disable: boolean
  contentType: ContentType
  suggestionQuery: DocumentNode
  queryId: string
  filteredBy: string
  orderBy: string
  orderDirection: 'asc' | 'desc'
  setSearchParam: (search: string) => void
  onSearchChange: (search: string) => void
  searchTag?: string
}

const SearchComponent: React.FC<SearchComponentProps> = ({
  disable,
  contentType,
  suggestionQuery,
  queryId,
  filteredBy,
  orderBy,
  orderDirection,
  setSearchParam,
  onSearchChange,
  searchTag,
}) => {
  const [search, setSearch, debouncedSearch] = useDebouncedSearch(onSearchChange)
  const { data: suggested } = useQuery(suggestionQuery, {
    skip: debouncedSearch === '',
    variables: {
      search: suggestionFunction(contentType, debouncedSearch),
      filter: filterFunction(contentType, filteredBy),
      order: sortFunction(contentType, orderBy, orderDirection),
      queryId,
    },
  })

  // Update parent search param when search changes
  useEffect(() => {
    setSearchParam(debouncedSearch)
  }, [debouncedSearch, setSearchParam])

  useEffect(() => {
    if (searchTag) {
      setSearch(searchTag)
    }
  }, [searchTag])

  const allSuggestions = useMemo(() => {
    if (!suggested) return []

    switch (contentType) {
      case 'creators':
        return (suggested as GetSuggestedCreators).user.map(user => ({
          label: `@${user.name}`,
          value: user.name,
        }))

      case 'collections':
        return (suggested as GetSuggestedCollections).collection.map(collection => ({
          label: collection.name,
          value: collection.id,
        }))

      case 'collection_items':
        return (suggested as GetSuggestedCollectionItems).token.map(item => ({
          label: itemNameWithId(item.metadata.name, item.nativeId),
          value: item.id,
        }))

      case 'items':
        return (suggested as GetSuggestedItems).token.map(item => ({
          label: itemNameWithId(item.metadata.name, item.nativeId),
          value: item.id,
        }))

      case 'user_published':
        return (suggested as GetSuggestedCreatedItems).token.map(item => ({
          label: itemNameWithId(item.metadata.name, item.nativeId),
          value: item.id,
        }))

      case 'user_owned':
        return (suggested as GetSuggestedOwnedItems).balance.map(({ token }) => ({
          label: itemNameWithId(token.metadata.name, token.nativeId),
          value: token.id,
        }))

      case 'user_earned':
        return (suggested as GetSuggestedEarnedItems).balance.map(({ token }) => ({
          label: itemNameWithId(token.metadata.name, token.nativeId),
          value: token.id,
        }))

      default:
        throw new Error('Content type not implemented')
    }
  }, [suggested, contentType])

  const handleSearchChange = useCallback(
    (searchVal: string) => {
      setSearch(searchVal)
    },
    [setSearch]
  )

  return (
    <Autocomplete
      leftSection={<IconSearch size={18} />}
      rightSection={
        search !== '' && (
          <ActionIcon onClick={() => setSearch('')} style={{ marginRight: '10px' }}>
            <CloseIcon size="18px" />
          </ActionIcon>
        )
      }
      data={allSuggestions}
      placeholder="Search"
      onChange={handleSearchChange}
      value={search}
      limit={20}
      disabled={disable}
    />
  )
}

export default SearchComponent
